import {
  type AtlasDocument,
  buildEdited,
  can,
  diagnosedDepartments,
  diffEdits,
  editsOf,
} from '@atlas-control/shared';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { ExternalLink, Pencil, RotateCcw, Send, Trash2, Undo2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { DisabledReason } from '@/components/disabled-reason';
import { ReviewCard } from '@/components/review-card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  finalReviewQuery,
  mergeLintQuery,
  useFinalReviewMutations,
} from '@/features/final-review/api';
import { DocumentView } from '@/features/final-review/document-view';
import { fieldLabel } from '@/features/final-review/field-label';
import { SECTIONS } from '@/features/final-review/sections';
import { runQuery } from '@/features/runs/api';
import { useMe } from '@/lib/auth';
import { formatDateTime } from '@/lib/format';
import { toastError, toastSuccess } from '@/lib/notify';

export const Route = createFileRoute('/_app/runs/$runId/final-review')({
  component: FinalReviewTab,
});

function FinalReviewTab() {
  const { runId } = Route.useParams();
  const { user } = useMe();
  const { data: run } = useSuspenseQuery(runQuery(runId));
  const [version, setVersion] = useState<number | undefined>(undefined);
  const ml = useQuery(mergeLintQuery(runId, version));
  const state = useQuery(finalReviewQuery(runId));
  const m = useFinalReviewMutations(runId);

  const [editing, setEditing] = useState(false);
  // While editing: departments taken off the board, and prose edits by stable path.
  const [removed, setRemoved] = useState<Set<string>>(new Set());
  const [edits, setEdits] = useState<Map<string, string>>(new Map());
  const [saveOpen, setSaveOpen] = useState(false);
  const [note, setNote] = useState('');
  const [compareWith, setCompareWith] = useState<number | null>(null);
  const [discardOpen, setDiscardOpen] = useState(false);
  const other = useQuery({
    ...mergeLintQuery(runId, compareWith ?? undefined),
    enabled: compareWith !== null,
  });

  const stopEditing = () => {
    setEditing(false);
    setRemoved(new Set());
    setEdits(new Map());
  };

  const content = ml.data?.content ?? null;
  const original = ml.data?.original ?? null;
  // Rebuilt from the original on every change: removing a department re-derives
  // the document exactly as Atlas does, and restoring one is always allowed.
  const draft = useMemo<AtlasDocument | null>(
    () => (editing && original ? buildEdited(original, removed, edits) : null),
    [editing, original, removed, edits],
  );
  const shown = editing && draft ? draft : content;
  const diagnosed = useMemo(
    () => (original ? diagnosedDepartments(original) : new Set<string>()),
    [original],
  );
  const changes = useMemo(
    () => (content && draft ? diffEdits(content, draft) : []),
    [content, draft],
  );
  const comparison = useMemo(
    () =>
      content && original && other.data?.content ? diffEdits(other.data.content, content) : null,
    [content, original, other.data],
  );

  if (ml.isPending)
    return <p className="text-sm text-muted-foreground">Loading the final output…</p>;
  if (!ml.data?.ready || !shown || !ml.data.version) {
    return (
      <p className="text-sm text-muted-foreground">
        The final output appears here when Atlas has finished the run.
      </p>
    );
  }

  const isLatest = ml.data.version.version === ml.data.latestVersion;
  const canEdit =
    original !== null &&
    can(user.role, 'final.edit') &&
    isLatest &&
    !ml.data.locked &&
    run.status === 'awaiting_final_review';
  const pushReason = !state.data?.canPush
    ? 'Atlas cannot accept edited output yet (pending P3)'
    : !state.data.unpushedVersion
      ? 'Atlas already has the latest version'
      : null;
  const reviewBlocked =
    run.status !== 'awaiting_final_review' && run.status !== 'review_done'
      ? 'The run is not at final review'
      : editing
        ? 'Save or discard your edits first'
        : state.data?.canPush && state.data.unpushedVersion
          ? `Send version ${state.data.unpushedVersion} to Atlas first`
          : !can(user.role, 'final.review')
            ? 'Your role cannot review the final output'
            : null;

  const startEditing = () => {
    if (!original || !content) return;
    const from = editsOf(original, content);
    setRemoved(from.removed);
    setEdits(from.edits);
    setEditing(true);
  };
  const toggleDepartment = (id: string) =>
    setRemoved((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="grid gap-6 xl:grid-cols-[12rem_minmax(0,1fr)_19rem]">
      <nav className="hidden xl:block" aria-label="Sections">
        <ul className="sticky top-20 space-y-1 text-sm">
          {SECTIONS.map(([id, label]) => (
            <li key={id}>
              <a
                href={`#${id}`}
                className="block rounded px-2 py-1 text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                {label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      <div className="min-w-0 space-y-4">
        <div className="sticky top-14 z-10 flex flex-wrap items-center gap-2 border-b bg-background/95 py-2 backdrop-blur">
          <Select
            value={String(ml.data.version.version)}
            onValueChange={(v) => {
              stopEditing();
              setVersion(Number(v) === ml.data?.latestVersion ? undefined : Number(v));
            }}
          >
            <SelectTrigger className="w-64" aria-label="Version">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ml.data.versions.map((v) => (
                <SelectItem key={v.version} value={String(v.version)}>
                  v{v.version} · {v.createdBy ? v.createdBy.name : 'Atlas'}
                  {v.version === ml.data?.latestVersion ? ' (latest)' : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {ml.data.versions.length > 1 && (
            <Select
              value={compareWith ? String(compareWith) : ''}
              onValueChange={(v) => setCompareWith(Number(v))}
            >
              <SelectTrigger className="w-44" aria-label="Compare with">
                <SelectValue placeholder="Compare with…" />
              </SelectTrigger>
              <SelectContent>
                {ml.data.versions
                  .filter((v) => v.version !== ml.data?.version?.version)
                  .map((v) => (
                    <SelectItem key={v.version} value={String(v.version)}>
                      v{v.version}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}
          <div className="flex-1" />
          {run.canvasBoardUrl && (
            <Button variant="outline" asChild>
              <a
                href={run.canvasBoardUrl}
                target="_blank"
                rel="noreferrer noopener"
                title="View only"
              >
                Canvas board <ExternalLink />
              </a>
            </Button>
          )}
          {editing ? (
            <>
              <span className="text-sm text-muted-foreground">{changes.length} change(s)</span>
              <Button variant="outline" onClick={stopEditing}>
                Discard changes
              </Button>
              <Button disabled={changes.length === 0} onClick={() => setSaveOpen(true)}>
                Save as v{(ml.data.latestVersion ?? 0) + 1}
              </Button>
            </>
          ) : (
            <>
              <DisabledReason
                reason={
                  canEdit
                    ? null
                    : ml.data.locked
                      ? 'The review is marked done; undo it to edit'
                      : !isLatest
                        ? 'Only the latest version can be edited'
                        : !original
                          ? 'This run was finished before Atlas v2; its document can only be read'
                          : 'Editing is not available'
                }
              >
                <Button variant="outline" disabled={!canEdit} onClick={startEditing}>
                  <Pencil /> Edit
                </Button>
              </DisabledReason>
              {canEdit && ml.data.version.version > 1 && (
                <Button variant="outline" onClick={() => setDiscardOpen(true)}>
                  <Undo2 /> Discard v{ml.data.version.version}
                </Button>
              )}
              <DisabledReason reason={pushReason}>
                <Button
                  variant="outline"
                  disabled={Boolean(pushReason) || m.push.isPending}
                  onClick={() =>
                    m.push.mutate(undefined, {
                      onSuccess: () => toastSuccess('Sent to Atlas'),
                      onError: (e) => toastError(e),
                    })
                  }
                >
                  <Send /> Send to Atlas
                </Button>
              </DisabledReason>
            </>
          )}
        </div>

        <p className="text-xs text-muted-foreground">
          v{ml.data.version.version} ·{' '}
          {ml.data.version.createdBy
            ? `edited by ${ml.data.version.createdBy.name}`
            : 'as produced by Atlas'}{' '}
          · {formatDateTime(ml.data.version.createdAt)}
          {ml.data.version.note ? ` · “${ml.data.version.note}”` : ''}
          {ml.data.version.pushedToAtlasAt ? ' · in Atlas' : ' · not in Atlas'}
        </p>
        <section id="findings" className="scroll-mt-20">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Findings</CardTitle>
            </CardHeader>
            <CardContent className="text-sm">
              {ml.data.findings.length ? (
                <ul className="list-disc space-y-1 pl-5">
                  {ml.data.findings.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground">Atlas reported nothing left out.</p>
              )}
            </CardContent>
          </Card>
        </section>
        {!original && (
          <Alert>
            <AlertDescription>
              This run was finished before Atlas v2. Its document is shown as stored and cannot be
              edited here.
            </AlertDescription>
          </Alert>
        )}
        {editing && original && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Departments</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="text-muted-foreground">
                Text can be edited anywhere below, and whole departments taken off the board.
                Removing one takes its solutions, risks and grunt work with it and recounts the
                rest, exactly as Atlas does. Ids, enums and numbers are fixed. Version 1 always
                keeps every department.
              </p>
              <ul className="divide-y">
                {original.departments.map((d) => {
                  const isRemoved = removed.has(d.id);
                  const locked = diagnosed.has(d.id);
                  return (
                    <li key={d.id} className="flex items-center justify-between gap-3 py-1.5">
                      <span className={isRemoved ? 'text-muted-foreground line-through' : ''}>
                        {d.name} <span className="font-mono text-xs">{d.id}</span>
                      </span>
                      <DisabledReason
                        reason={
                          locked && !isRemoved
                            ? 'Named in the executive diagnosis, so it stays on the board'
                            : null
                        }
                      >
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={locked && !isRemoved}
                          aria-label={`${isRemoved ? 'Restore' : 'Remove'} department ${d.name}`}
                          onClick={() => toggleDepartment(d.id)}
                        >
                          {isRemoved ? (
                            <>
                              <RotateCcw /> Restore
                            </>
                          ) : (
                            <>
                              <Trash2 /> Remove
                            </>
                          )}
                        </Button>
                      </DisabledReason>
                    </li>
                  );
                })}
              </ul>
            </CardContent>
          </Card>
        )}
        {original ? (
          <DocumentView
            doc={shown}
            editing={editing}
            edits={edits}
            onEdit={(path, value) => setEdits((prev) => new Map(prev).set(path, value))}
          />
        ) : (
          <pre className="max-h-[70vh] overflow-auto rounded-md bg-muted p-3 text-xs">
            {JSON.stringify(shown, null, 2)}
          </pre>
        )}
      </div>

      <div className="space-y-4">
        <div className="sticky top-20 space-y-4">
          <ReviewCard
            title="Final review"
            description="Check the board as the client will see it. Marking this done allows the credentials email."
            reviewedAt={state.data?.review?.reviewedAt ?? null}
            reviewedBy={state.data?.review?.reviewedBy.name ?? null}
            notes={state.data?.review?.notes ?? null}
            blockedReason={reviewBlocked}
            undoBlockedReason={
              run.status === 'review_done' ? null : 'The credentials email has been sent'
            }
            pending={m.review.isPending || m.undo.isPending}
            onReview={(notes) =>
              m.review.mutate(notes, {
                onSuccess: () => toastSuccess('Final review done'),
                onError: (e) => toastError(e),
              })
            }
            onUndo={() => m.undo.mutate(undefined, { onError: (e) => toastError(e) })}
          />
          {state.data?.unpushedVersion && !state.data.canPush && (
            <Alert>
              <AlertDescription className="text-xs">
                Version {state.data.unpushedVersion} has edits Atlas cannot take yet (pending P3):
                the client’s board shows the version Atlas produced.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        title={`Save ${changes.length} change(s) as version ${(ml.data.latestVersion ?? 0) + 1}?`}
        description={
          <div className="space-y-2">
            <ul className="max-h-48 list-disc space-y-1 overflow-y-auto pl-5 text-xs">
              {changes.slice(0, 30).map((c) => (
                <li key={c.path}>
                  {fieldLabel(c.path)}
                  {c.after === '(removed)' ? ' (removed)' : ''}
                </li>
              ))}
            </ul>
            <Input
              placeholder="Note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              aria-label="Version note"
            />
          </div>
        }
        confirmLabel="Save version"
        pending={m.save.isPending}
        onConfirm={() =>
          draft &&
          ml.data?.latestVersion &&
          m.save.mutate(
            { baseVersion: ml.data.latestVersion, content: draft, note: note.trim() || undefined },
            {
              onSuccess: (res) => {
                toastSuccess(`Saved version ${res.latestVersion}`);
                setSaveOpen(false);
                stopEditing();
                setNote('');
                setVersion(undefined);
              },
              onError: (e) => {
                setSaveOpen(false);
                toastError(e);
              },
            },
          )
        }
      />

      <ConfirmDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        title={`Discard version ${ml.data.version.version}?`}
        description={
          <p>
            Its edits are thrown away and version {ml.data.version.version - 1} becomes the latest.
            {ml.data.version.pushedToAtlasAt
              ? ` It is on the Canvas board now: send version ${ml.data.version.version - 1} to Atlas to put the board back.`
              : ''}{' '}
            Version 1, what Atlas produced, is always kept.
          </p>
        }
        confirmLabel={`Discard v${ml.data.version.version}`}
        destructive
        pending={m.discard.isPending}
        onConfirm={() =>
          ml.data?.version &&
          m.discard.mutate(ml.data.version.version, {
            onSuccess: (res) => {
              toastSuccess(`Discarded; version ${res.latestVersion} is the latest`);
              setDiscardOpen(false);
              setVersion(undefined);
            },
            onError: (e) => {
              setDiscardOpen(false);
              toastError(e);
            },
          })
        }
      />

      <Sheet open={compareWith !== null} onOpenChange={(o) => !o && setCompareWith(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle>
              v{compareWith} → v{ml.data.version.version}
            </SheetTitle>
            <SheetDescription>
              {comparison ? `${comparison.length} field(s) differ` : 'Loading…'}
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-3 px-4 pb-6">
            {comparison?.map((c) => (
              <Card key={c.path}>
                <CardHeader>
                  <CardTitle className="text-sm">{fieldLabel(c.path)}</CardTitle>
                </CardHeader>
                <CardContent className="grid gap-2 text-sm md:grid-cols-2">
                  <p className="rounded bg-danger-soft/50 p-2 whitespace-pre-wrap">
                    {c.before || '-'}
                  </p>
                  <p className="rounded bg-success-soft/50 p-2 whitespace-pre-wrap">
                    {c.after || '-'}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
